import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { AppLayout } from "@/components/intel/Layout";
import { fetchLiveEvents, fetchSourceHealth } from "@/lib/intel/feeds.functions";
import { WatchlistPanel } from "@/components/intel/WatchlistPanel";
import { LiveMeshPanel } from "@/components/intel/LiveMeshPanel";
import { Pause, Play, Wifi, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/monitor")({
  head: () => ({ meta: [{ title: "Live Monitoring · CARIO INTEL" }] }),
  component: MonitorPage,
});

function MonitorPage() {
  const [paused, setPaused] = useState(false);
  const [query, setQuery] = useState("");
  const eventsFn = useServerFn(fetchLiveEvents);
  const healthFn = useServerFn(fetchSourceHealth);
  const events = useQuery({ queryKey: ["live-events"], queryFn: () => eventsFn(), refetchInterval: paused ? false : 20_000, staleTime: 15_000 });
  const health = useQuery({ queryKey: ["source-health"], queryFn: () => healthFn(), refetchInterval: paused ? false : 60_000, staleTime: 30_000 });
  const list = events.data ?? [];
  const healthRows = health.data ?? [];
  const online = healthRows.filter((s) => s.status === "online").length;
  const degraded = healthRows.filter((s) => s.status === "degraded").length;
  const filtered = list.filter((e) => `${e.source} ${e.title} ${e.meta}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <AppLayout>
    <ModuleShell
      eyebrow="Live Monitoring"
      title="Real-time OSINT signal stream"
      description="Neural triage over a 4,900+ endpoint live monitoring mesh (adverse media, sanctions, enforcement, CERT advisories, filings, chains, infrastructure), plus the raw public firehose and collector health probes."
      meta={<button onClick={() => setPaused((v) => !v)} className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground ring-glow">{paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}{paused ? "Resume" : "Pause"}</button>}
    >
      <WatchlistPanel />
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile label="Collectors online" value={online} tone="success" />
        <StatTile label="Live events buffered" value={list.length} tone="cyan" />
        <StatTile label="Degraded feeds" value={degraded} tone="amber" />
        <StatTile label="Legal scope" value="PUBLIC" tone="success" />
      </div>
      <LiveMeshPanel paused={paused} />
      <div className="glass rounded-xl p-3">
        <div className="flex items-center gap-2 rounded-md border border-panel-border bg-background/40 px-3 py-2">
          <Wifi className="h-4 w-4 text-cyan" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter live stream by source, signal, status…" className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
        </div>
        <div className="mt-3 divide-y divide-panel-border overflow-hidden rounded-md border border-panel-border">
          {events.isLoading && <div className="p-6 text-center text-sm text-muted-foreground">Connecting to public OSINT feeds…</div>}
          {filtered.map((event) => (
            <a key={event.id} href={event.url} target="_blank" rel="noopener noreferrer" className="grid gap-2 px-3 py-2 text-xs hover:bg-panel/60 md:grid-cols-12">
              <div className="font-mono text-cyan md:col-span-2">{event.source}</div>
              <div className="md:col-span-7 truncate">{event.title}</div>
              <div className="text-muted-foreground md:col-span-2 truncate">{event.meta}</div>
              <div className="font-mono text-muted-foreground md:col-span-1 text-right">{event.ts.slice(11, 19)}</div>
            </a>
          ))}
        </div>
      </div>
      <div className="glass rounded-xl p-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan px-1 pb-2">Collector health probes</div>
        <div className="overflow-hidden rounded-md border border-panel-border divide-y divide-panel-border">
          {healthRows.map((s) => (
            <div key={s.name} className="grid gap-2 px-3 py-2 text-xs md:grid-cols-12">
              <div className="md:col-span-5">{s.name}</div>
              <div className="text-muted-foreground md:col-span-2">{s.category}</div>
              <div className="font-mono text-muted-foreground md:col-span-2">{s.latency}ms</div>
              <div className="font-mono text-muted-foreground md:col-span-1">HTTP {s.httpStatus ?? "—"}</div>
              <div className={`font-mono md:col-span-2 text-right ${s.status === "online" ? "text-success" : s.status === "degraded" ? "text-amber" : "text-danger"}`}>{s.status.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </div>
    </ModuleShell>
    </AppLayout>
  );
}